import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Platform, Animated } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useExploreStore, useExploreLightboxStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { pickMusicGradient } from '@/constants/Colors';

const isWeb = Platform.OS === 'web';

const CARD_HEIGHT = 168;
const CARD_HEIGHT_SM = 140;
const CARD_GAP = 10;
const MAX_DISPLAY = 12;

// Per-mediaType aspect ratio fallbacks when the item has no metadata.
// Music → square card so the gradient/icon composition feels intentional.
// Video → cinematic 16:9. Image → soft portrait that reads gallery-like.
const FALLBACK_ASPECT = { image: 0.82, video: 16 / 9, music: 1 };

const getAspect = (item) => {
  const a = item?.metadata?.aspectRatio;
  if (a && Number.isFinite(a) && a > 0) return Math.min(Math.max(a, 0.5), 2.2);
  if (item?.metadata?.width && item?.metadata?.height) {
    return Math.min(Math.max(item.metadata.width / item.metadata.height, 0.5), 2.2);
  }
  return FALLBACK_ASPECT[item?.mediaType] || 1;
};

const SkeletonCard = ({ width, height, colors }) => {
  const opacity = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 1, duration: 700, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 0.5, duration: 700, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[
        styles.card,
        {
          width,
          height,
          backgroundColor: colors.solid_color_tertiary,
          opacity,
        },
      ]}
    />
  );
};

const MusicCardCover = React.memo(({ item }) => {
  if (item.thumbnailPath || item.cdnPath) {
    return (
      <Image
        source={{ uri: item.thumbnailPath || item.cdnPath }}
        style={styles.cardMedia}
        contentFit="cover"
        placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
        transition={180}
        recyclingKey={item.id}
        priority="low"
      />
    );
  }
  const gradient = item.gradient || pickMusicGradient(item.id);
  return (
    <LinearGradient
      colors={gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.cardMedia}
    >
      <View style={styles.musicIconWrap}>
        <Icon name="music" size={28} color="rgba(255,255,255,0.85)" solid />
      </View>
    </LinearGradient>
  );
});

const ShowcaseCard = React.memo(({ item, onPress, colors, height }) => {
  const [hovered, setHovered] = useState(false);
  const aspect = getAspect(item);
  const width = Math.round(height * aspect);
  const isVideo = item.mediaType === 'video';
  const isMusic = item.mediaType === 'music';

  const handlePress = useCallback(() => onPress?.(item), [item, onPress]);

  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={isWeb ? () => setHovered(true) : undefined}
      onHoverOut={isWeb ? () => setHovered(false) : undefined}
      style={({ pressed }) => [
        styles.card,
        {
          width,
          height,
          backgroundColor: colors.solid_color_tertiary,
          borderColor: hovered
            ? colors.border_color_primary
            : colors.border_color_secondary,
        },
        isWeb && hovered && styles.cardHovered,
        pressed && styles.cardPressed,
      ]}
    >
      <View style={styles.mediaClip}>
        <View style={[styles.mediaZoom, isWeb && hovered && styles.mediaZoomHovered]}>
          {isMusic ? (
            <MusicCardCover item={item} />
          ) : (
            <Image
              source={{ uri: item.thumbnailPath || item.cdnPath }}
              style={styles.cardMedia}
              contentFit="cover"
              placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
              transition={180}
              recyclingKey={item.id}
              priority="low"
            />
          )}
        </View>
      </View>

      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0.85)']}
        locations={[0, 0.45, 1]}
        style={styles.gradient}
        pointerEvents="none"
      />

      {(isVideo || isMusic) && (
        <View style={styles.mediaBadge} pointerEvents="none">
          <Icon
            name={isVideo ? 'play' : 'music'}
            size={9}
            color="#fff"
            solid
          />
        </View>
      )}

      {item.flags?.isPlusShowcase && (
        <View style={styles.plusBadge} pointerEvents="none">
          <Icon name="bolt" size={8} color="#fff" solid />
          <Text size="xxs" weight="semiBold" color="white">Plus</Text>
        </View>
      )}

      <View style={styles.captionRow} pointerEvents="none">
        <Text
          size="xxs"
          weight="medium"
          color="white"
          numberOfLines={2}
          style={styles.captionText}
        >
          {item.displayName || item.prompt}
        </Text>
      </View>
    </Pressable>
  );
});

function ModelExploreShowcase({ model, variant = 'page', limit = MAX_DISPLAY, onItemOpen }) {
  const { colors } = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();

  const items = useExploreStore(state => state.itemsByModelId[model?.id]);
  const isLoading = useExploreStore(state => !!state.itemsByModelLoading[model?.id]);
  const fetchedAt = useExploreStore(state => state.itemsByModelFetchedAt[model?.id] || 0);
  const { fetchItemsByModel } = useExploreStore(state => state.actions);
  const { openExploreLightbox } = useExploreLightboxStore(state => state.actions);

  const [hovered, setHovered] = useState(false);
  const scrollRef = useRef(null);
  const scrollOffsetRef = useRef(0);
  const contentWidthRef = useRef(0);
  const containerWidthRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const isSmall = isXS || isSM;
  const cardHeight = isSmall ? CARD_HEIGHT_SM : CARD_HEIGHT;

  useEffect(() => {
    if (model?.id) fetchItemsByModel(model.id);
  }, [model?.id, fetchItemsByModel]);

  const visibleItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items.slice(0, limit);
  }, [items, limit]);

  const updateArrows = useCallback(() => {
    const offset = scrollOffsetRef.current;
    const maxScroll = contentWidthRef.current - containerWidthRef.current;
    setCanScrollLeft(offset > 1);
    setCanScrollRight(maxScroll > 1 && offset < maxScroll - 1);
  }, []);

  const handleScroll = useCallback((e) => {
    scrollOffsetRef.current = e.nativeEvent.contentOffset.x;
    updateArrows();
  }, [updateArrows]);

  const handleContentSizeChange = useCallback((w) => {
    contentWidthRef.current = w;
    updateArrows();
  }, [updateArrows]);

  const handleLayout = useCallback((e) => {
    containerWidthRef.current = e.nativeEvent.layout.width;
    updateArrows();
  }, [updateArrows]);

  const scrollBy = useCallback((dir) => {
    const amount = (cardHeight * 1.4 + CARD_GAP) * 2;
    const offset = scrollOffsetRef.current + dir * amount;
    scrollRef.current?.scrollTo({ x: Math.max(0, offset), animated: true });
  }, [cardHeight]);

  const handleItemPress = useCallback((item) => {
    trackEvent('model_page', 'showcase_item_open', item.id);
    onItemOpen?.();
    openExploreLightbox(item, items || []);
  }, [items, openExploreLightbox, onItemOpen]);

  const handleSeeAll = useCallback(() => {
    if (!model?.slug) return;
    trackEvent('model_page', 'showcase_see_all', model.slug);
    onItemOpen?.();
    router.push(`/explore?model=${encodeURIComponent(model.slug)}`);
  }, [model?.slug, router, onItemOpen]);

  const skeletonWidths = useMemo(
    () => [0.82, 1, 16 / 9, 0.82, 1.2].map(a => Math.round(cardHeight * a)),
    [cardHeight],
  );

  // Show nothing once we know there's nothing to show. While the very first
  // fetch is pending we render a subtle skeleton row so the section doesn't
  // pop in awkwardly after layout has settled.
  const hasFetched = fetchedAt > 0;
  const hasItems = visibleItems.length > 0;
  if (!model?.id) return null;
  if (hasFetched && !isLoading && !hasItems) return null;

  const showSkeletons = !hasFetched && isLoading;

  const containerStyle = [
    styles.container,
    variant === 'modal' && styles.containerModal,
  ];

  return (
    <View
      style={containerStyle}
      {...(isWeb ? {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      } : {})}
    >
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={[styles.headerIcon, { backgroundColor: colors.solid_color_tertiary }]}>
            <Icon name="sparkles" size={10} color={colors.text_color_primary} solid />
          </View>
          <Text size="xs" weight="semiBold" color="primary" style={styles.headerLabel}>
            {t('modals.modelDetails.madeWithThisModel')}
          </Text>
          {hasItems && (
            <View style={[styles.countPill, { backgroundColor: colors.solid_color_tertiary }]}>
              <Text size="xxs" weight="semiBold" color="secondary">
                {visibleItems.length}
              </Text>
            </View>
          )}
        </View>
        {hasItems && model?.slug && (
          <Pressable onPress={handleSeeAll} style={styles.seeAllBtn} hitSlop={8}>
            <Text size="xxs" weight="semiBold" color="secondary">
              {t('modals.modelDetails.seeAll')}
            </Text>
            <Icon name="arrow-right" size={9} color={colors.text_color_secondary} />
          </Pressable>
        )}
      </View>

      <View style={styles.scrollWrapper} onLayout={handleLayout}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          onContentSizeChange={handleContentSizeChange}
          contentContainerStyle={styles.scrollContent}
          decelerationRate="fast"
        >
          {showSkeletons
            ? skeletonWidths.map((w, i) => (
              <SkeletonCard key={`sk-${i}`} width={w} height={cardHeight} colors={colors} />
            ))
            : visibleItems.map(item => (
              <ShowcaseCard
                key={item.id}
                item={item}
                onPress={handleItemPress}
                colors={colors}
                height={cardHeight}
              />
            ))}

          {hasItems && model?.slug && (
            <Pressable
              onPress={handleSeeAll}
              style={[
                styles.seeAllCard,
                {
                  height: cardHeight,
                  width: Math.round(cardHeight * 0.66),
                  borderColor: colors.border_color_secondary,
                  backgroundColor: colors.solid_color_secondary,
                },
              ]}
            >
              <View style={[styles.seeAllIcon, { backgroundColor: colors.solid_color_tertiary }]}>
                <Icon name="arrow-right" size={14} color={colors.text_color_primary} />
              </View>
              <Text size="xxs" weight="semiBold" color="primary" style={styles.seeAllCardLabel}>
                {t('modals.modelDetails.seeAll')}
              </Text>
            </Pressable>
          )}
        </ScrollView>

        {isWeb && hovered && canScrollLeft && (
          <Pressable
            onPress={() => scrollBy(-1)}
            style={[
              styles.arrowButton,
              styles.arrowLeft,
              { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary },
            ]}
          >
            <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
          </Pressable>
        )}
        {isWeb && hovered && canScrollRight && (
          <Pressable
            onPress={() => scrollBy(1)}
            style={[
              styles.arrowButton,
              styles.arrowRight,
              { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary },
            ]}
          >
            <Icon name="chevron-right" size={12} color={colors.text_color_primary} />
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 10,
  },
  containerModal: {
    marginTop: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
    gap: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  headerIcon: {
    width: 18,
    height: 18,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerLabel: {
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  countPill: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 999,
    minWidth: 18,
    alignItems: 'center',
  },
  seeAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 6,
    ...(isWeb && { cursor: 'pointer' }),
  },
  scrollWrapper: {
    position: 'relative',
  },
  scrollContent: {
    flexDirection: 'row',
    gap: CARD_GAP,
    paddingHorizontal: 2,
    paddingBottom: 4,
  },
  card: {
    overflow: 'hidden',
    borderRadius: 10,
    borderWidth: 1,
    position: 'relative',
    ...(isWeb && {
      cursor: 'pointer',
      transitionProperty: 'transform, box-shadow, border-color',
      transitionDuration: '200ms',
      transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    }),
  },
  cardHovered: {
    ...(isWeb && {
      transform: [{ translateY: -2 }],
      boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    }),
  },
  cardPressed: {
    opacity: 0.92,
  },
  mediaClip: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
    borderRadius: 10,
  },
  mediaZoom: {
    flex: 1,
    ...(isWeb && {
      transitionProperty: 'transform',
      transitionDuration: '600ms',
      transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    }),
  },
  mediaZoomHovered: {
    ...(isWeb && { transform: [{ scale: 1.06 }] }),
  },
  cardMedia: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  musicIconWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '70%',
  },
  captionRow: {
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 8,
  },
  captionText: {
    lineHeight: 14,
  },
  mediaBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  plusBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'rgba(139, 92, 246, 0.92)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  seeAllCard: {
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  seeAllIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  seeAllCardLabel: {
    textAlign: 'center',
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -16,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    zIndex: 10,
    ...(isWeb && {
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }),
  },
  arrowLeft: {
    left: 4,
  },
  arrowRight: {
    right: 4,
  },
});

export default React.memo(ModelExploreShowcase);
